<script setup lang="ts">
import { ref } from 'vue';
import { LinkButton, viewportSize, useTranslation } from '@hfc/ui-core';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import HeadingNavSubItem from '@/shared/infrastructure/components/heading/HeadingNavSubItem.vue';
import { MenuItem } from '@/shared/domain/MenuItem';

defineProps<{
  item: MenuItem,
  smallHeight?: boolean
}>();

const { viewport } = viewportSize();
const { t } = useTranslation();

const popoverClose = ref<(() => void) | undefined>();
const popoverOpen = ref(false);

function onClickOutside (): void {
  if (!viewport.value.isMobile && popoverOpen.value) {
    popoverClose.value?.();
  }
}

function syncPopoverState (open: boolean, close: () => void): void {
  popoverClose.value = close;
  popoverOpen.value = open;
}

defineExpose({
  onClickOutside
});
</script>
<template>
  <LinkButton v-if="item.href" :href="item.href" :ignore-styles="true" class="flex items-center md:hover:bg-blue-100 group px-2 lg:px-4">
    <span class="items-center text-base md:text-sm lg:text-base font-normal focus:outline-none focus:text-blue-dark text-white md:text-blue-500 md:group-hover:text-blue dark:text-white border-transparent border-b-2">
      {{ t(item.label) }}
    </span>
  </LinkButton>
  <Popover v-else v-slot="{ open, close }" class="overflow-x-hidden md:overflow-x-visible md:flex md:hover:bg-blue-100 group">
    <div class="contents" :ref="() => syncPopoverState(open, close)">
      <PopoverButton ref="buttonRef" class="group w-full inline-flex items-center text-base md:text-sm lg:text-base font-normal focus:outline-none focus:-outline-offset-2 focus:text-blue-dark text-white md:text-blue-500 dark:text-white dark:group-hover:md:text-blue-500 border-transparent border-b-2 max-h-[112px]" :class="{ 'md:bg-blue-100 md:dark:text-blue-500': open }">
        <div class="flex items-center w-full px-2 lg:px-4">
          <span class="pr-2 lg:pr-3 h-full md:py-11">
            {{ t(item.label) }}
          </span>
          <hfc-icon v-if="item.subMenu.length" :icon="`chevron-${open ? 'up' : 'down'}`" class="w-4 h-auto relative" :class="{'top-0.5': open}" />
        </div>
      </PopoverButton>
      <PopoverPanel v-if="item.subMenu.length" ref="dropdownRef" :class="{ 'md:mt-20': smallHeight, 'md:mt-24': !smallHeight }" class=" mt- md:absolute z-20 md:px-5 text-center right-0 left-0 flex flex-col md:flex-row mt-6 md:w-screen max-w-7xl mx-auto [&>.sub-item:nth-of-type(even)]:bg-blue-100 [&>.sub-item:nth-of-type(odd)]:bg-white">
        <HeadingNavSubItem v-for="i in item.subMenu" :key="i.label" :item="i" :class="item.customClass" class="flex-grow py-6 md:py-8 shadow sub-item" />
      </PopoverPanel>
    </div>
  </Popover>
</template>
